import {
	Component,
	computed,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
	ElementRef,
	ViewChild,
	inject,
	signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
	Observable,
	Subject,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	of,
	takeUntil,
} from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import {
	FuseNavigationService,
	FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserSettingsService } from 'app/core/services/user-settings.service';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { AiChatComponent } from '../../../common/ai-chat/ai-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
	GroupedSearchItemDto,
	SearchClient,
	SearchItemDto,
} from '../../../../core/services/api.service';
import { FormControl } from '@angular/forms';
import { SearchDropdownComponent } from 'app/layout/common/search-dropdown/search-dropdown.component';
import { MinimumTierDirective } from '../../../../directives/minimum-tier.directive';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { UserSettingsStore } from 'app/store/user-setting.store';
import { CompanySwitcherComponent } from '../../../common/company-switcher/company-switcher.component';
import { SupportContextService } from 'app/core/auth/support-context.service';
import { AuthService } from 'app/core/auth/auth.service';

type NavigationAppearance = 'default' | 'dense';

@Component({
	selector: 'dense-layout',
	templateUrl: './dense.component.html',
	encapsulation: ViewEncapsulation.None,
	imports: [
		FuseLoadingBarComponent,
		FuseVerticalNavigationComponent,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		SearchComponent,
		ShortcutsComponent,
		UserComponent,
		RouterOutlet,
		MatAutocompleteModule,
		SearchDropdownComponent,
		AiChatComponent,
		MinimumTierDirective,
		HasPermissionDirective,
		CompanySwitcherComponent,
	],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
	private _userSettingsStore = inject(UserSettingsStore);
	private readonly _supportContextService = inject(SupportContextService);
	private readonly _authService = inject(AuthService);

	isScreenSmall: boolean;
	navigation: Navigation;
	navigationAppearance = signal<NavigationAppearance>('default');
	readonly isSupportDelegationActive = this._supportContextService.isActive;
	readonly supportDelegationTooltip = computed(() => {
		const ctx = this._supportContextService.context();
		const email = ctx?.actAsEmail;
		return email ? `Impersonating: ${email}` : 'Impersonating';
	});
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	isSpinnerLoading: boolean = true;

	searchControl: FormControl = new FormControl();
	searchResult$: Observable<GroupedSearchItemDto[]>;
	searchResultLength: number = 0;
	searchItem: SearchItemDto;

	@ViewChild('aiChat') aiChat: AiChatComponent;

	/**
	 * Constructor
	 */
	constructor(
		private _navigationService: NavigationService,
		private _fuseMediaWatcherService: FuseMediaWatcherService,
		private _fuseNavigationService: FuseNavigationService,
		private _userSettingsService: UserSettingsService,
		private _searchClient: SearchClient,
		private _elementRef: ElementRef // Add this line
	) {
		let appearance = this._userSettingsStore.getUserSettingByKey(
			'sidebar-collapsed'
		) as NavigationAppearance;

		if (!appearance) {
			appearance = 'default';
			this._userSettingsService.setByKeyValue(
				'sidebar-collapsed',
				appearance
			);
		}

		this.navigationAppearance.set(appearance);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Getter for current year
	 */
	get currentYear(): number {
		return new Date().getFullYear();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		// Subscribe to navigation data
		this._navigationService.navigation$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((navigation: Navigation) => {
				this.navigation = navigation;
			});

		// Subscribe to media changes
		this._fuseMediaWatcherService.onMediaChange$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(({ matchingAliases }) => {
				// Check if the screen is small
				this.isScreenSmall = !matchingAliases.includes('md');

				// Change the navigation appearance
				const appearance = this.isScreenSmall
					? 'default'
					: ((this._userSettingsStore.getUserSettingByKey(
							'sidebar-collapsed'
					  ) ?? 'default') as NavigationAppearance);

				this.navigationAppearance.set(appearance);
			});

		this.searchControl.valueChanges
			.pipe(
				filter((query) => !query || String(query).length >= 3),
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this._unsubscribeAll)
			)
			.subscribe({
				next: (value) => this.onSearch(value),
			});
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Toggle navigation
	 *
	 * @param name
	 */
	toggleNavigation(name: string): void {
		if (this.isScreenSmall) {
			// Get the navigation
			const navigation =
				this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
					name
				);

			if (navigation) {
				// Toggle the opened status
				navigation.toggle();
			}

			return;
		}

		// Toggle the collapsed status
		let appearance = this.navigationAppearance();
		appearance = appearance == 'default' ? 'dense' : 'default';
		this._userSettingsService.setByKeyValue(
			'sidebar-collapsed',
			appearance
		);
		this.navigationAppearance.set(appearance);
	}

	onSearch(searchPhrase: string) {
		if (!searchPhrase) {
			this.searchResultLength = 0;
			this.searchResult$ = of([]);

			return;
		}

		this.searchResult$ = this._searchClient.search(searchPhrase).pipe(
			map((result) => {
				this.searchResultLength = result.length;
				return result;
			})
		);
	}

	toggleAIAssistant() {
		this.aiChat.toggle();
	}

	clearDelegationAndLogout(): void {
		this._supportContextService.clearContext();
		this._authService.signOut().subscribe();
	}
}
